import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GameStateResponse } from '@/lib/api';

interface UseAdminGameStateReturn {
  gameState: GameStateResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminGameState(userId: string, keyword: string): UseAdminGameStateReturn {
  const [gameState, setGameState] = useState<GameStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchState = useCallback(async () => {
    if (!userId || !keyword) {
      setLoading(false);
      return;
    }
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.adminGetGameState(userId, keyword);
      if (fetchIdRef.current !== id) return;
      setGameState(result.gameState);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load game state');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [userId, keyword]);

  const refresh = useCallback(() => {
    setError(null);
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    if (userId && keyword) {
      refresh();
    }
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh, userId, keyword]);

  return { gameState, loading, error, refresh };
}
